<template>
  <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
    <!-- Total Students -->
    <div v-if="canViewStudents" class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
      <div class="flex justify-between items-start">
        <div>
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Total Students</p>
          <h3 class="text-3xl font-bold text-gray-900">{{ formatNumber(stats?.totalStudents || 0) }}</h3>
        </div>
        <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
           <i class="pi pi-users text-xl"></i>
        </div>
      </div>
      <div class="flex items-center gap-4 text-xs mt-4">
        <div class="flex items-center gap-1.5">
           <span class="w-2 h-2 rounded-full bg-primary"></span>
           <span class="text-gray-600">Male ({{ malePercentage }}%)</span>
        </div>
        <div class="flex items-center gap-1.5">
           <span class="w-2 h-2 rounded-full bg-yellow-400"></span>
           <span class="text-gray-600">Female ({{ femalePercentage }}%)</span>
        </div>
      </div>
    </div>

    <!-- Staff Members -->
    <div v-if="canViewStaff" class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
      <div class="flex justify-between items-start">
        <div>
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Staff Members</p>
          <h3 class="text-3xl font-bold text-gray-900">{{ formatNumber(stats?.totalStaff || 0) }}</h3>
        </div>
        <div class="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
           <i class="pi pi-briefcase text-xl"></i>
        </div>
      </div>
      <div class="flex items-center gap-2 text-xs mt-4 text-primary font-medium" v-if="stats?.newStaffCount">
         <i class="pi pi-arrow-up text-xs"></i>
         <span>{{ stats.newStaffCount }} new hired this month</span>
      </div>
      <div class="flex items-center gap-2 text-xs mt-4 text-gray-400 font-medium" v-else>
         <span>No new hires this month</span>
      </div>
    </div>

     <!-- Attendance -->
    <div v-if="canViewAttendance" class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
      <div class="flex justify-between items-start">
        <div>
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Today's Attendance</p>
          <h3 class="text-3xl font-bold text-gray-900">{{ attendancePercentage }}%</h3>
        </div>
        <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
           <i class="pi pi-check-circle text-xl"></i>
        </div>
      </div>
      <div class="w-full bg-gray-100 rounded-full h-1.5 mt-4">
        <div class="bg-primary h-1.5 rounded-full" :style="{ width: `${attendancePercentage}%` }"></div>
      </div>
      <p class="text-xs text-gray-500 mt-2">
        <span class="font-bold text-gray-700">{{ stats?.attendanceToday?.present || 0 }}</span> Present
        <span class="mx-1">•</span>
        <span class="font-bold text-gray-700">{{ stats?.attendanceToday?.absent || 0 }}</span> Absent
      </p>
    </div>

    <!-- Current Week -->
    <div class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
      <div class="flex justify-between items-start">
        <div>
          <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Current Week</p>
          <h3 class="text-3xl font-bold text-gray-900" data-testid="current-week-headline">{{ weekCard.headline }}</h3>
        </div>
        <div class="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600">
           <i class="pi pi-calendar text-xl"></i>
        </div>
      </div>
      <div class="text-xs text-gray-500 mt-4" data-testid="current-week-detail">
        {{ weekCard.detail }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DashboardStats } from '@/types/composition-functions';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { currentWeekCard } from '@/utils/dashboardWeek';

const { currentUser } = storeToRefs(useUserStore());

const props = defineProps<{
  stats?: DashboardStats | null
}>();

const isAdmin = computed(() =>
  can(currentUser.value, AppPermissions.Permissions.Actions.View, AppPermissions.Permissions.Resource),
);
const canViewStudents = computed(() =>
  isAdmin.value || can(currentUser.value, AppPermissions.Students.Actions.View, AppPermissions.Students.Resource),
);
const canViewStaff = computed(() =>
  isAdmin.value || can(currentUser.value, AppPermissions.Teachers.Actions.View, AppPermissions.Teachers.Resource),
);
const canViewAttendance = computed(() =>
  isAdmin.value || can(currentUser.value, AppPermissions.Attendance.Actions.View, AppPermissions.Attendance.Resource),
);

const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

const malePercentage = computed(() => {
  if (!props.stats?.totalStudents) return 0;
  return Math.round((props.stats.maleStudentsCount / props.stats.totalStudents) * 100);
});

const femalePercentage = computed(() => {
  if (!props.stats?.totalStudents) return 0;
  return Math.round((props.stats.femaleStudentsCount / props.stats.totalStudents) * 100);
});

const attendancePercentage = computed(() => props.stats?.attendanceToday?.percentage || 0);

const weekCard = computed(() => currentWeekCard(props.stats));

</script>
